/**
 * Hook to read sidebar permissions + program access from the
 * IntegratedSidebarProvider (one fetch, shared across all consumers).
 *
 * Public API is unchanged from when this hook owned its own state:
 *   sidebarItems, permissions, loading, error,
 *   canSeeSidebarItem, hasPermission, canAccessProgram,
 *   getAccessiblePrograms, canSeeSection
 *
 * Previously each call site re-fetched /admin/my-sidebar + /programs
 * independently. On admin landing that produced ~12 redundant requests
 * and let different components see different state when the backend
 * was cold. See 2026-05-16 dashboard flip-flop bug.
 */

import { useContext } from 'react';
import { useAuth } from '../AuthContext';
import { apiLog } from '../utils/debugConfig.js';
import { IntegratedSidebarContext } from '../contexts/IntegratedSidebarContext.jsx';
import { isCaseManagementProgramName, isHousingSupportProgramName, isResidentialProgramName, matchesProgramType } from '../utils/programTypeAccess.js';

// Safe defaults used when the hook is called outside the provider
// (e.g. some unit tests, or login-only routes). Behaves like a brand-new
// unauthenticated session — nothing visible, no permissions.
const EMPTY_STATE = {
  sidebarItems: [],
  permissions: {},
  programAccess: null,
  programs: [],
  loading: false,
  error: null,
  permissionsStatus: null,
};

export const useIntegratedSidebar = () => {
  const ctx = useContext(IntegratedSidebarContext) || EMPTY_STATE;
  const { user } = useAuth() || {};
  const { sidebarItems, permissions, programAccess, programs, loading, error, permissionsStatus } = ctx;

  const canSeeSidebarItem = (itemKey) => sidebarItems.includes(itemKey);

  // super_admin holds every permission, including ones added after their Role row was
  // seeded. ROLE_TEMPLATES.super_admin already derives its permission map from the whole
  // catalog, and authMiddleware grants super_admin universal access at the API — but a
  // stored Role row is only written at seed time, so a permission added later is absent
  // from `permissions` until someone re-seeds. Without this bypass the API allows the
  // action while the UI hides the control, and every new permission needs a manual grant.
  // Mirrors AuthContext.hasPermission and canAccessProgram just below.
  const hasPermission = (permission) => {
    if (user?.role === 'super_admin') return true;
    return permissions[permission] === true;
  };

  // Program access for a program key ('behavioral-health', 'adult-day-services',
  // 'home-health', '245d-waiver-services', 'residential-services'): the server's
  // decision (same as the API) when available, else the program's permission.
  const PROGRAM_KEY_PERMISSION = {
    'behavioral-health': 'access_behavioral_health',
    'adult-day-services': 'access_adult_day',
    'home-health': 'access_home_health',
    '245d-waiver-services': 'view_245d_basic',
    'residential-services': 'view_residential',
    'case-management': 'view_case_management',
    'housing-support': 'view_housing_support',
  };
  const hasProgramAccess = (programKey) => {
    if (user?.role === 'super_admin') return true;
    if (programAccess && programKey in programAccess) return programAccess[programKey] === true;
    return hasPermission(PROGRAM_KEY_PERMISSION[programKey]);
  };

  const canAccessProgram = (programName) => {
    if (!programName) {
      apiLog('⚠️ canAccessProgram: empty program name');
      return false;
    }

    if (user?.role === 'super_admin') {
      apiLog(`👑 Super admin bypass for program: "${programName}"`);
      return true;
    }

    const nameCheck = programName.toLowerCase();
    apiLog(`🔍 Checking access for program: "${programName}"`);

    // Targeted case management names often say "mental health" too, so this
    // also runs before the Behavioral Health match.
    if (isCaseManagementProgramName(nameCheck)) {
      const hasAccess = hasProgramAccess('case-management');
      apiLog(`  → Case Management program: ${hasAccess ? '✅' : '❌'}`);
      return hasAccess;
    }
    // Co-responder program names include "mental health", so this must run
    // before the broader Behavioral Health match.
    if (nameCheck.includes('co-responder') || nameCheck.includes('coresponder')) {
      const hasAccess = hasPermission('view_co_responder_documents');
      apiLog(`  → Co-Responder program: ${hasAccess ? '✅' : '❌'}`);
      return hasAccess;
    }
    if (nameCheck.includes('behavioral') || nameCheck.includes('mental health')) {
      const hasAccess = hasProgramAccess('behavioral-health');
      apiLog(`  → Behavioral Health program: ${hasAccess ? '✅' : '❌'}`);
      return hasAccess;
    }
    // 245D Basic / Intensive: same permission the API's requireProgramAccess uses.
    if (nameCheck.includes('245d')) {
      const hasAccess = hasProgramAccess('245d-waiver-services');
      apiLog(`  → 245D program: ${hasAccess ? '✅' : '❌'}`);
      return hasAccess;
    }
    if (nameCheck.includes('adult day')) {
      const hasAccess = hasProgramAccess('adult-day-services');
      apiLog(`  → Adult Day program: ${hasAccess ? '✅' : '❌'}`);
      return hasAccess;
    }
    if (nameCheck.includes('home health')) {
      const hasAccess = hasProgramAccess('home-health');
      apiLog(`  → Home Health program: ${hasAccess ? '✅' : '❌'}`);
      return hasAccess;
    }
    if (isResidentialProgramName(nameCheck)) {
      const hasAccess = hasProgramAccess('residential-services');
      apiLog(`  → Residential Services program: ${hasAccess ? '✅' : '❌'}`);
      return hasAccess;
    }
    if (isHousingSupportProgramName(nameCheck)) {
      const hasAccess = hasProgramAccess('housing-support');
      apiLog(`  → Housing Support program: ${hasAccess ? '✅' : '❌'}`);
      return hasAccess;
    }
    // Housing Stabilization Services ended 10/31/2025. A tenant's historical
    // program of that kind stays hidden from non-admins, as it always was (the
    // permission it used to check was never in the catalog).
    if (nameCheck.includes('housing stabilization')) {
      apiLog('  → Housing Stabilization program (ended 10/31/2025): ❌');
      return false;
    }

    const hasManage = hasPermission('manage_programs');
    apiLog(`  → Other program: ${hasManage ? '✅' : '❌'} (manage_programs: ${hasManage})`);
    return hasManage;
  };

  const getAccessiblePrograms = () => {
    const accessibleNames = [];

    if (user?.role === 'super_admin') {
      apiLog('Super admin - full program access');
      return ['all'];
    }

    if (hasPermission('manage_programs')) {
      apiLog('User has manage_programs - full program access');
      return ['all'];
    }

    const permissionToProgramType = {
      access_behavioral_health: ['behavioral_health', 'behavioral-health'],
      access_adult_day: ['adult_day_services', 'adult_day', 'adult-day-services', 'day-services'],
      access_home_health: ['home_health', 'home-health'],
      view_housing_support: ['housing_support', 'housing-support', 'Housing Support'],
      view_co_responder_documents: ['co-responder', 'co_responder', 'mh_co_responder'],
      view_residential: ['residential_services', 'residential-services', 'Residential Services'],
      view_case_management: ['case_management', 'case-management', 'Case Management'],
    };

    Object.entries(permissionToProgramType).forEach(([permission, programTypes]) => {
      if (hasPermission(permission)) {
        const matchingPrograms = programs.filter((p) =>
          matchesProgramType(p.programType, programTypes)
        );
        matchingPrograms.forEach((p) => {
          if (!accessibleNames.includes(p.name)) {
            accessibleNames.push(p.name);
            apiLog(`Added "${p.name}" to accessible programs (type: ${p.programType})`);
          }
        });
      }
    });

    apiLog('Accessible programs:', accessibleNames.length > 0 ? accessibleNames : 'None');
    return accessibleNames;
  };

  const canSeeSection = (section) => {
    const permissionMap = {
      patients: ['view_patients', 'create_patients', 'edit_patients'],
      clinical: ['clinical_access', 'view_assessments', 'write_progress_notes', 'view_documents'],
      visits: ['view_visits', 'manage_visits', 'create_visits'],
      billing: ['view_billing', 'manage_claims', 'manage_authorizations'],
      accounting: ['view_accounting', 'export_accounting'],
      analytics: ['view_analytics', 'view_reports'],
      providers: ['view_providers', 'manage_providers'],
      users: ['view_users', 'manage_users'],
      programs: ['view_programs', 'manage_programs'],
      roles: ['manage_roles', 'manage_permissions'],
      schedules: ['view_schedules', 'manage_schedules'],
      reports: ['view_reports'],
      timeTracking: ['access_time_tracking', 'view_all_time_entries', 'manage_all_time_entries'],
      appointments: ['view_appointments', 'manage_appointments', 'create_appointments', 'edit_appointments'],
      clinicalChart: ['view_clinical_chart', 'clinical_access'],
      serviceLines: ['view_service_lines', 'edit_service_lines', 'view_home_health_service_lines'],
      communication: ['use_messaging', 'manage_notifications', 'access_support_tickets'],
      compliance: [
        'manage_compliance',
        'view_compliance_reports',
        'view_compliance_checklists',
        'view_billing_compliance',
        'quality_reviews',
      ],
    };

    const requiredPerms = permissionMap[section] || [];
    const hasAccess = requiredPerms.some((perm) => hasPermission(perm));
    apiLog(`🔍 Section access check for "${section}":`, hasAccess ? '✅ Allowed' : '❌ Denied');
    return hasAccess;
  };

  return {
    sidebarItems,
    permissions,
    programs,
    loading,
    error,
    // 'loaded' | 'denied' | 'failed' | 'loading': is `permissions` the server's answer?
    permissionsStatus,
    canSeeSidebarItem,
    hasPermission,
    hasProgramAccess,
    canAccessProgram,
    getAccessiblePrograms,
    canSeeSection,
  };
};
